import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from './AuthProvider'

type FlagKey = 'what_if' | 'optimizers'

type FlagsState = Record<FlagKey, boolean>

interface FeatureFlagContextValue {
  flags: FlagsState
  isEnabled: (key: FlagKey) => boolean
}

const defaultFlags: FlagsState = {
  what_if: false,
  optimizers: false,
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined)

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
  const { role, session } = useAuth() as any
  const [flags, setFlags] = useState<FlagsState>(defaultFlags)

  useEffect(() => {
    let mounted = true
    const loadFlags = async () => {
      try {
        const [{ data: flagsData }, { data: userData }] = await Promise.all([
          supabase.from('feature_flags').select('key, enabled, targeting'),
          session?.user?.id
            ? supabase.from('user_feature_flags').select('key, enabled').eq('user_id', session.user.id)
            : Promise.resolve({ data: null } as any),
        ])
        if (!flagsData) return
        const next = { ...defaultFlags }
        for (const row of flagsData as any[]) {
          const key = row.key as FlagKey
          if (!key) continue
          let enabled = !!row.enabled
          if (!enabled && row.targeting && role) {
            const roles: string[] | undefined = row.targeting?.roles
            if (Array.isArray(roles) && roles.includes(role)) enabled = true
          }
          if (key in next) next[key] = enabled
        }
        if (Array.isArray(userData)) {
          for (const u of userData) {
            const key = u.key as FlagKey
            if (key in next) next[key] = !!u.enabled
          }
        }
        if (mounted) setFlags(next)
      } catch {
        // ignore and keep defaults
      }
    }
    loadFlags()
    return () => {
      mounted = false
    }
  }, [role, session?.user?.id])

  const value = useMemo<FeatureFlagContextValue>(() => ({
    flags,
    isEnabled: (key) => !!flags[key],
  }), [flags])

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  )
}

export function useFeature() {
  const ctx = useContext(FeatureFlagContext)
  if (!ctx) throw new Error('useFeature must be used within FeatureFlagProvider')
  return ctx
}


